import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  ADMIN_FEATURES,
  describePermissions,
  isFullAdmin,
  sanitizeFeatureIds,
  type AdminFeatureId,
  type AdminUser,
  type Role,
} from "@tih/shared";
import { useAuth } from "../../../context/AuthContext";
import {
  ApiError,
  adminCreateUser,
  adminDeleteUser,
  adminFetchUsers,
  adminResetUserPassword,
  adminUpdateUser,
} from "../../../api/client";

const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  role: "USER" as Role,
  fullAccess: true,
  permissions: [] as AdminFeatureId[],
};

function groupedFeatures() {
  const groups = new Map<string, typeof ADMIN_FEATURES>();
  for (const feature of ADMIN_FEATURES) {
    const list = groups.get(feature.group) ?? [];
    list.push(feature);
    groups.set(feature.group, list);
  }
  return [...groups.entries()];
}

export function AdminSettingsUsersPage() {
  const { user: currentUser } = useAuth();
  const actorIsFull = isFullAdmin(currentUser);
  const grantable = useMemo(
    () =>
      actorIsFull
        ? ADMIN_FEATURES.map((f) => f.id)
        : sanitizeFeatureIds(currentUser?.permissions),
    [actorIsFull, currentUser?.permissions],
  );
  const formRef = useRef<HTMLElement>(null);
  const [rows, setRows] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [resetId, setResetId] = useState<number | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const showForm = showAdd || editingId != null;

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setRows(await adminFetchUsers());
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function startAdd() {
    setEditingId(null);
    setResetId(null);
    setForm({
      ...EMPTY_FORM,
      fullAccess: actorIsFull,
      permissions: actorIsFull ? [] : [...grantable],
    });
    setShowAdd(true);
    setSuccess(null);
    setError(null);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function startEdit(row: AdminUser) {
    setShowAdd(false);
    setResetId(null);
    setEditingId(row.id);
    setForm({
      name: row.name,
      email: row.email,
      password: "",
      role: row.role,
      fullAccess: isFullAdmin(row),
      permissions: sanitizeFeatureIds(row.permissions),
    });
    setSuccess(null);
    setError(null);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function cancelForm() {
    setShowAdd(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
  }

  function toggleFeature(id: AdminFeatureId) {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(id)
        ? f.permissions.filter((p) => p !== id)
        : [...f.permissions, id],
    }));
  }

  async function saveUser(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);
    const permissions =
      form.role === "ADMIN" ? (form.fullAccess ? [] : form.permissions) : [];
    if (form.role === "ADMIN" && !form.fullAccess && permissions.length === 0) {
      setError("Select at least one feature, or give full access.");
      setBusy(false);
      return;
    }
    try {
      if (editingId != null) {
        const updated = await adminUpdateUser(editingId, {
          name: form.name.trim(),
          email: form.email.trim(),
          role: form.role,
          permissions,
        });
        setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        setSuccess("User updated.");
        cancelForm();
      } else {
        const created = await adminCreateUser({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          role: form.role,
          permissions,
        });
        setRows((prev) => [...prev, created]);
        setSuccess("User created.");
        cancelForm();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save user");
    } finally {
      setBusy(false);
    }
  }

  async function submitReset(e: FormEvent) {
    e.preventDefault();
    if (resetId == null) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await adminResetUserPassword(resetId, { password: resetPassword });
      setSuccess("Password reset.");
      setResetId(null);
      setResetPassword("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reset password");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    if (!confirm("Delete this user account? This cannot be undone.")) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await adminDeleteUser(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      if (editingId === id) cancelForm();
      setSuccess("User deleted.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete user");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <p className="admin-card-sub" style={{ margin: 0 }}>
          {rows.length} account{rows.length === 1 ? "" : "s"}. Admins can have full access or
          only the features you tick. You cannot delete yourself or the last admin.
        </p>
        {!showForm && (
          <button type="button" className="btn btn-primary" onClick={startAdd}>
            + Add user
          </button>
        )}
      </div>

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12, marginBottom: 16 }}>
          {success}
        </div>
      )}

      {loading ? (
        <div style={{ opacity: 0.5 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ opacity: 0.6 }}>No users yet.</div>
      ) : (
        <div className="admin-table-wrap" style={{ marginBottom: 24 }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Access</th>
                <th>Joined</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.name}
                    {currentUser?.id === r.id ? " (you)" : ""}
                  </td>
                  <td>
                    <a href={`mailto:${r.email}`}>{r.email}</a>
                  </td>
                  <td>{describePermissions(r)}</td>
                  <td>{new Date(r.createdAt).toLocaleString()}</td>
                  <td className="admin-row-actions">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={busy}
                      onClick={() => startEdit(r)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={busy}
                      onClick={() => {
                        setResetId(r.id);
                        setResetPassword("");
                        setShowAdd(false);
                        setEditingId(null);
                      }}
                    >
                      Reset password
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={busy || currentUser?.id === r.id}
                      onClick={() => void remove(r.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <section ref={formRef} className="admin-card">
          <h2 className="admin-card-title">
            {editingId != null ? "Edit user" : "Add user"}
          </h2>
          <form onSubmit={(e) => void saveUser(e)} className="admin-blog-form">
            <div className="admin-blog-form-fields">
              <label>
                <span>Name</span>
                <input
                  value={form.name}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  type="email"
                  value={form.email}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
              </label>
              {editingId == null && (
                <label>
                  <span>Password</span>
                  <input
                    type="password"
                    value={form.password}
                    disabled={busy}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    minLength={8}
                    required
                  />
                </label>
              )}
              <label>
                <span>Role</span>
                <select
                  value={form.role}
                  disabled={busy}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, role: e.target.value as Role }))
                  }
                >
                  <option value="USER">USER — public site only</option>
                  <option value="ADMIN">ADMIN — admin desk</option>
                </select>
              </label>
            </div>

            {form.role === "ADMIN" && (
              <div className="admin-perm-block">
                <label className="admin-perm-full">
                  <input
                    type="checkbox"
                    checked={form.fullAccess}
                    disabled={busy || !actorIsFull}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, fullAccess: e.target.checked }))
                    }
                  />
                  <span>Full access to every admin feature</span>
                </label>
                {!actorIsFull && (
                  <p className="admin-muted">
                    You can only grant features you already have.
                  </p>
                )}
                {!form.fullAccess && (
                  <div className="admin-perm-groups">
                    {groupedFeatures().map(([group, features]) => (
                      <fieldset key={group} className="admin-perm-group">
                        <legend>{group}</legend>
                        {features.map((feature) => {
                          const allowed = grantable.includes(feature.id);
                          return (
                            <label key={feature.id} className="admin-perm-item">
                              <input
                                type="checkbox"
                                checked={form.permissions.includes(feature.id)}
                                disabled={busy || !allowed}
                                onChange={() => toggleFeature(feature.id)}
                              />
                              <span>
                                <strong>{feature.label}</strong>
                                <em>{feature.description}</em>
                              </span>
                            </label>
                          );
                        })}
                      </fieldset>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="admin-blog-form-actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Saving…" : editingId != null ? "Save changes" : "Create user"}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={cancelForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      {resetId != null && (
        <section className="admin-card">
          <h2 className="admin-card-title">Reset password</h2>
          <form onSubmit={(e) => void submitReset(e)} className="admin-blog-form">
            <div className="admin-blog-form-fields">
              <label>
                <span>New password</span>
                <input
                  type="password"
                  value={resetPassword}
                  disabled={busy}
                  onChange={(e) => setResetPassword(e.target.value)}
                  minLength={8}
                  required
                />
              </label>
            </div>
            <div className="admin-blog-form-actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Saving…" : "Set new password"}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => {
                  setResetId(null);
                  setResetPassword("");
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
